// Approximate traditional artist pigments for the Kubelka-Munk model.
//
// IMPORTANT: these are NOT measured spectral data. Each pigment's absorption
// spectrum shape is a smooth parametric curve placed where the pigment is
// documented (qualitatively) to absorb: e.g. cadmium yellow's sharp absorption
// edge below ~500 nm, phthalocyanine blue's broad red absorption. The absorption
// amplitude is then fitted at load time so that a 1-unit masstone layer over
// white paper approximates a visual target colour (also approximate, see
// PAINT_ENGINE_ORIGINALITY.md, "Pigment data"). Scattering is fixed per pigment
// from its documented opacity class. Replace with measured K/S data when a
// licensed dataset is available; the rest of the engine does not change.

import { BAND_COUNT, BAND_CENTRES, spectrumToLinearRgb, linearRgbToLab, srgbToLinear, hexToSrgb, deltaE76, BAND_TO_LINEAR_RGB } from './ColourConversion.js';
import { kmLayerReflectance, saundersonBody, saundersonInverse } from './SpectralMixing.js';

const gauss = (l, mu, sigma) => Math.exp(-0.5 * ((l - mu) / sigma) ** 2);
// Logistic step: ~1 for wavelengths below l0, ~0 above.
const shortPass = (l, l0, w) => 1 / (1 + Math.exp((l - l0) / w));
const longPass = (l, l0, w) => 1 - shortPass(l, l0, w);

// White paper reflectance (slightly warm, slight blue loss), used as the default substrate.
export function paperSpectrum(tone = 0.86, warmth = 0.04) {
  const out = new Float32Array(BAND_COUNT);
  for (let band = 0; band < BAND_COUNT; band += 1) {
    const l = BAND_CENTRES[band];
    out[band] = Math.max(0.02, Math.min(0.98, tone - warmth * shortPass(l, 450, 25)));
  }
  return out;
}

// Physical properties (0..1 unless noted):
//   granulation: tendency to settle into paper valleys
//   staining: resistance to lifting once deposited
//   density: settling speed in water
//   opacityClass: 'transparent' | 'semi' | 'opaque' (sets scattering)
const PIGMENT_DEFINITIONS = [
  {
    id: 'titanium_white', name: 'Titanium White', code: 'PW6', target: '#f3f3ee',
    shape: (l) => shortPass(l, 412, 8), base: 0.02, scatter: 14.0, scatterPower: 0.35,
    granulation: 0.05, staining: 0.2, density: 0.55, opacityClass: 'opaque',
  },
  {
    id: 'ivory_black', name: 'Ivory Black', code: 'PBk9', target: '#1f1d1c',
    shape: (l) => 0.05 * shortPass(l, 500, 60), base: 1.0, scatter: 0.25, scatterPower: 0.2,
    granulation: 0.35, staining: 0.4, density: 0.5, opacityClass: 'semi',
  },
  {
    id: 'ultramarine', name: 'Ultramarine Blue', code: 'PB29', target: '#25297c',
    shape: (l) => gauss(l, 582, 46), base: 0.05, scatter: 0.35, scatterPower: 1.0,
    granulation: 0.9, staining: 0.15, density: 0.7, opacityClass: 'transparent',
  },
  {
    id: 'cobalt_blue', name: 'Cobalt Blue', code: 'PB28', target: '#2448a0',
    shape: (l) => gauss(l, 605, 45) + 0.35 * longPass(l, 640, 20), base: 0.04, scatter: 1.2, scatterPower: 0.8,
    granulation: 0.75, staining: 0.2, density: 0.8, opacityClass: 'semi',
  },
  {
    id: 'phthalo_blue', name: 'Phthalo Blue', code: 'PB15', target: '#0f2d5e',
    shape: (l) => longPass(l, 585, 14) + 0.12 * gauss(l, 560, 18), base: 0.03, scatter: 0.12, scatterPower: 1.0,
    granulation: 0.0, staining: 0.95, density: 0.2, opacityClass: 'transparent',
  },
  {
    id: 'cadmium_red', name: 'Cadmium Red', code: 'PR108', target: '#c4271f',
    shape: (l) => shortPass(l, 586, 6), base: 0.01, scatter: 3.5, scatterPower: 0.4,
    granulation: 0.2, staining: 0.3, density: 0.85, opacityClass: 'opaque',
  },
  {
    id: 'alizarin_crimson', name: 'Alizarin Crimson', code: 'PR83', target: '#701526',
    shape: (l) => gauss(l, 535, 38) + 0.4 * gauss(l, 470, 28), base: 0.02, scatter: 0.12, scatterPower: 1.0,
    granulation: 0.0, staining: 0.85, density: 0.3, opacityClass: 'transparent',
  },
  {
    id: 'yellow_ochre', name: 'Yellow Ochre', code: 'PY43', target: '#c08b30',
    shape: (l) => shortPass(l, 522, 13) + 0.6 * shortPass(l, 470, 15), base: 0.05, scatter: 2.2, scatterPower: 0.5,
    granulation: 0.55, staining: 0.25, density: 0.75, opacityClass: 'semi',
  },
  {
    id: 'burnt_sienna', name: 'Burnt Sienna', code: 'PBr7', target: '#8c3d1d',
    shape: (l) => shortPass(l, 592, 17) + 0.7 * shortPass(l, 505, 22), base: 0.06, scatter: 0.6, scatterPower: 0.6,
    granulation: 0.6, staining: 0.4, density: 0.7, opacityClass: 'transparent',
  },
  {
    id: 'raw_umber', name: 'Raw Umber', code: 'PBr7', target: '#4b3c2b',
    shape: (l) => shortPass(l, 560, 55), base: 0.5, scatter: 0.9, scatterPower: 0.5,
    granulation: 0.7, staining: 0.35, density: 0.75, opacityClass: 'semi',
  },
  {
    id: 'viridian', name: 'Viridian', code: 'PG18', target: '#1e6b56',
    shape: (l) => longPass(l, 600, 18) + 0.5 * shortPass(l, 450, 16), base: 0.03, scatter: 0.3, scatterPower: 1.0,
    granulation: 0.65, staining: 0.3, density: 0.6, opacityClass: 'transparent',
  },
  {
    id: 'cadmium_yellow', name: 'Cadmium Yellow', code: 'PY35', target: '#f2c31c',
    shape: (l) => shortPass(l, 505, 7), base: 0.005, scatter: 3.5, scatterPower: 0.4,
    granulation: 0.2, staining: 0.3, density: 0.85, opacityClass: 'opaque',
  },
];

const WHITE_SUBSTRATE = paperSpectrum(0.88, 0.0);
const MASSTONE_AMOUNT = 1.0;

function buildSpectra(definition, kScale, kBase) {
  const K = new Float32Array(BAND_COUNT);
  const S = new Float32Array(BAND_COUNT);
  for (let band = 0; band < BAND_COUNT; band += 1) {
    const l = BAND_CENTRES[band];
    K[band] = kScale * definition.shape(l) + kBase;
    S[band] = definition.scatter * Math.pow(550 / l, definition.scatterPower);
  }
  return { K, S };
}

export function layerDisplayRgb(K, S, amount, substrate = WHITE_SUBSTRATE) {
  const spectrum = new Float32Array(BAND_COUNT);
  for (let band = 0; band < BAND_COUNT; band += 1) {
    spectrum[band] = saundersonBody(kmLayerReflectance(K[band] * amount, S[band] * amount, substrate[band]));
  }
  return spectrumToLinearRgb(spectrum);
}

function goldenSection(f, lo, hi, iterations = 40) {
  const phi = (Math.sqrt(5) - 1) / 2;
  let a = hi - phi * (hi - lo);
  let b = lo + phi * (hi - lo);
  let fa = f(a);
  let fb = f(b);
  for (let i = 0; i < iterations; i += 1) {
    if (fa < fb) {
      hi = b; b = a; fb = fa; a = hi - phi * (hi - lo); fa = f(a);
    } else {
      lo = a; a = b; fa = fb; b = lo + phi * (hi - lo); fb = f(b);
    }
  }
  return (lo + hi) / 2;
}

// Fits the absorption-feature amplitude and the absorption floor (log space,
// alternating golden-section line searches) so the masstone approaches the target.
function fitAbsorptionScale(definition) {
  const targetLab = linearRgbToLab(hexToSrgb(definition.target).map(srgbToLinear));
  const error = (logScale, logBase) => {
    const { K, S } = buildSpectra(definition, Math.exp(logScale), Math.exp(logBase));
    return deltaE76(linearRgbToLab(layerDisplayRgb(K, S, MASSTONE_AMOUNT)), targetLab);
  };
  let logScale = Math.log(5);
  let logBase = Math.log(Math.max(1e-4, definition.base));
  const baseUpper = Math.log(Math.max(0.5, definition.base * 40));
  const fitScale = (base) => goldenSection((v) => error(v, base), Math.log(0.05), Math.log(400));
  const fitBase = (scale) => goldenSection((v) => error(scale, v), Math.log(1e-4), baseUpper);
  for (let round = 0; round < 6; round += 1) {
    logScale = fitScale(logBase);
    logBase = fitBase(logScale);
  }
  return { kScale: Math.exp(logScale), kBase: Math.exp(logBase), fitDeltaE: error(logScale, logBase) };
}

function createPigment(definition) {
  const { kScale, kBase, fitDeltaE } = fitAbsorptionScale(definition);
  const { K, S } = buildSpectra(definition, kScale, kBase);
  return Object.freeze({
    id: definition.id,
    name: definition.name,
    code: definition.code,
    target: definition.target,
    K,
    S,
    granulation: definition.granulation,
    staining: definition.staining,
    density: definition.density,
    opacityClass: definition.opacityClass,
    calibration: Object.freeze({ kind: 'parametric-fit-to-visual-target', kScale, kBase, fitDeltaE }),
  });
}

export const PIGMENTS = Object.freeze(PIGMENT_DEFINITIONS.map(createPigment));
export const PIGMENTS_BY_ID = Object.freeze(Object.fromEntries(PIGMENTS.map((p) => [p.id, p])));

// Spectral upsampling of a display colour (upsampleLinearRgb), then KM inversion, for
// user-chosen colours that are not in the library. The pigment produced is
// plausible, not physical: it mixes subtractively but has no real counterpart.

// Spectral upsampling of a display colour: the reflectance spectrum r (12
// bands, 0.002..0.995) minimising |M r - rgb|^2 + SMOOTHNESS * sum (r[b+1]-r[b])^2,
// by projected gradient descent. The smoothness term ties together bands that
// barely affect RGB (spectrum ends), which a plain least-squares solve leaves
// unconstrained (that produced red-ended "blacks" that turned teal when thin).
// Neutral greys map exactly to flat spectra.
const SMOOTHNESS = 0.02;
const R_MIN = 0.002;
const R_MAX = 0.995;

export function upsampleLinearRgb(rgb) {
  const m = BAND_TO_LINEAR_RGB;
  const target = [0, 1, 2].map((c) => Math.max(0, Math.min(1, rgb[c])));
  const grey = (target[0] + target[1] + target[2]) / 3;
  const r = new Float64Array(BAND_COUNT).fill(Math.max(R_MIN, Math.min(R_MAX, grey)));
  // Step from a bound on the Hessian (|M^T M| + 4 * smoothness).
  let norm = 0;
  for (let c = 0; c < 3; c += 1) for (let b = 0; b < BAND_COUNT; b += 1) norm += m[c][b] * m[c][b];
  const step = 1 / (2 * (norm + 4 * SMOOTHNESS));
  const grad = new Float64Array(BAND_COUNT);
  for (let iteration = 0; iteration < 600; iteration += 1) {
    const err = [0, 0, 0];
    for (let c = 0; c < 3; c += 1) {
      let v = 0;
      for (let b = 0; b < BAND_COUNT; b += 1) v += m[c][b] * r[b];
      err[c] = v - target[c];
    }
    for (let b = 0; b < BAND_COUNT; b += 1) {
      let g = 2 * (m[0][b] * err[0] + m[1][b] * err[1] + m[2][b] * err[2]);
      if (b > 0) g += 2 * SMOOTHNESS * (r[b] - r[b - 1]);
      if (b < BAND_COUNT - 1) g += 2 * SMOOTHNESS * (r[b] - r[b + 1]);
      grad[b] = g;
    }
    let moved = 0;
    for (let b = 0; b < BAND_COUNT; b += 1) {
      const next = Math.max(R_MIN, Math.min(R_MAX, r[b] - step * grad[b]));
      moved += Math.abs(next - r[b]);
      r[b] = next;
    }
    if (moved < 1e-9) break;
  }
  return Float32Array.from(r);
}

export function createCustomPigment(hex, options = {}) {
  const linear = hexToSrgb(hex).map(srgbToLinear);
  const observed = upsampleLinearRgb(linear);
  const scatter = options.scatter ?? 1.5;
  const K = new Float32Array(BAND_COUNT);
  const S = new Float32Array(BAND_COUNT);
  for (let band = 0; band < BAND_COUNT; band += 1) {
    const r = Math.max(0.002, Math.min(0.995, saundersonInverse(observed[band])));
    // Infinite-layer KM inversion: K/S = (1 - R)^2 / (2R).
    S[band] = scatter;
    K[band] = scatter * ((1 - r) * (1 - r)) / (2 * r);
  }
  return Object.freeze({
    id: `custom_${String(hex).replace('#', '').toLowerCase()}`,
    name: `Custom ${hex}`,
    code: 'custom',
    target: hex,
    K,
    S,
    granulation: options.granulation ?? 0.3,
    staining: options.staining ?? 0.4,
    density: options.density ?? 0.5,
    opacityClass: 'semi',
    calibration: Object.freeze({ kind: 'rgb-upsampled-infinite-layer', kScale: 1, fitDeltaE: 0 }),
  });
}

export function resolvePigment(idOrHex) {
  if (PIGMENTS_BY_ID[idOrHex]) return PIGMENTS_BY_ID[idOrHex];
  if (/^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(String(idOrHex || ''))) {
    return createCustomPigment(String(idOrHex).startsWith('#') ? idOrHex : `#${idOrHex}`);
  }
  return null;
}
